﻿<script setup lang="ts">
    import { Ref, ref, onMounted, watch } from 'vue';
    import ThemesViewAndSelect from '@cwd/Themes/ThemesViewAndSelect.vue';
    import Modal from '@shared/components/Modal.vue';
    import koObsToRef from '@shared/utils/koObsToRef';
    import { type DesignTheme } from '@cwd/Themes/ThemesTypes';
    import Speakers from './Speakers.vue';
    import { type Speaker } from './EventTypes';

    type koTheme = {
        Name: string;
        DesignThemeId: string;
    };

    const emit = defineEmits<{
        (ev: 'addAsContactPerson', speaker: Speaker): void;
    }>();

    const props = defineProps<{
        event: KnockoutObservable<{
            DesignThemeId: KnockoutObservable<string>;
            EventId: KnockoutObservable<string>;
        }>;
        designTheme: {
            show: KnockoutObservable<boolean>;
            update: Function;
            themes: KnockoutObservableArray<koTheme>;
        };
        updateOrganizerSpeakerId: (newVal: string) => void;
    }>();

    const showSelectTheme: Ref<boolean> = koObsToRef(props.designTheme.show);
    const eventId: Ref<string> = koObsToRef(props.event().EventId);
    const showSelectContactModal = ref(false);

    onMounted(async () => {
        document.body.addEventListener('click', (event: MouseEvent) => {
            if (event.target instanceof HTMLElement && event.target.id === 'show-select-contact-modal') {
                showSelectContactModal.value = true;
            }
        });
    });

    const closeSelectThemeModal = () => {
        showSelectTheme.value = false;
    };

    const themeSelected = (id: string, theme: DesignTheme) => {
        props.designTheme.update(id, theme);
        closeSelectThemeModal();
    };

    const getThemes = (themes: DesignTheme[]) => {
        props.designTheme.themes(
            themes.map((theme) => {
                return {
                    Name: theme.Name,
                    DesignThemeId: theme.DesignThemeId as string,
                };
            })
        ); // ugly update of ko obs array
    };

    const updateOrganizerSpeakerId = (speakerId: string) => {
        props.updateOrganizerSpeakerId(speakerId);
        showSelectContactModal.value = false;
    };
</script>

<script lang="ts"></script>

<template>
    <Modal :title="$Resources.SelectThemeLabel" :show="showSelectTheme" @close="closeSelectThemeModal()" size="lg">
        <template #content>
            <ThemesViewAndSelect
                :currentThemeId="props.event().DesignThemeId()"
                :eventId="eventId"
                @themeCreated="closeSelectThemeModal()"
                @themeSelected="(newVal, theme) => themeSelected(newVal, theme)"
                @showModal="showSelectTheme = true"
                @getThemes="(newVal: DesignTheme[]) => getThemes(newVal)"></ThemesViewAndSelect>
        </template>
    </Modal>

    <Modal :title="$Resources.EventContactPersonSelect" :show="showSelectContactModal" @close="showSelectContactModal = false" size="xl">
        <template #content>
            <Speakers :addAsContactPerson="true" @addAsContactPerson="(speaker) => updateOrganizerSpeakerId(speaker.SpeakerId)"></Speakers>
        </template>
    </Modal>
</template>

<style lang="less"></style>
